/**
 * Summary numbers shown in Suspension → Summary (capsules) and compare table.
 * Jack-first for travel % / bottom-out / top-out / packing; metrics for balance / sag / roughness.
 * Roughness: 0–200 rider scale (0 calm, 50 typical, 100 quite rough) from roughnessScore + calibration.
 */

export const SUMMARY_DEFAULT_TRAVEL_FRONT_MM = 200;
export const SUMMARY_DEFAULT_TRAVEL_REAR_MM = 210;

/**
 * @param {object|null} jackResult — from runJackPackingAnalysisFromTelemetry
 * @param {object|null} metrics — from computeSuspensionMetrics
 * @param {number|null} [roughnessScore] — mapped 0–200 display score
 */
export function buildSummarySnapshot(jackResult, metrics, roughnessScore = null) {
  const jOk = jackResult?.ok === true;
  const ar = jOk ? jackResult.analysisResults : null;
  const stF = ar?.stats?.front;
  const stR = ar?.stats?.rear;
  const params = ar?.params;
  const frontTravelMm =
    Number.isFinite(Number(params?.frontTravel)) && Number(params.frontTravel) > 0
      ? Number(params.frontTravel)
      : SUMMARY_DEFAULT_TRAVEL_FRONT_MM;
  const rearTravelMm =
    Number.isFinite(Number(params?.rearTravel)) && Number(params.rearTravel) > 0
      ? Number(params.rearTravel)
      : SUMMARY_DEFAULT_TRAVEL_REAR_MM;

  let travelFrontPct = null;
  let travelRearPct = null;
  if (stF?.position && Number.isFinite(stF.position.max) && frontTravelMm > 0) {
    travelFrontPct = (stF.position.max / frontTravelMm) * 100;
  } else if (metrics?.max_front_travel != null && frontTravelMm > 0) {
    travelFrontPct = (metrics.max_front_travel / frontTravelMm) * 100;
  }
  if (stR?.position && Number.isFinite(stR.position.max) && rearTravelMm > 0) {
    travelRearPct = (stR.position.max / rearTravelMm) * 100;
  } else if (metrics?.max_rear_travel != null && rearTravelMm > 0) {
    travelRearPct = (metrics.max_rear_travel / rearTravelMm) * 100;
  }

  let bottomF = metrics?.bottom_out_front;
  let bottomR = metrics?.bottom_out_rear;
  let topF = metrics?.top_out_front;
  let topR = metrics?.top_out_rear;
  if (jOk && stF?.velocity?.compression && stR?.velocity?.compression) {
    bottomF = stF.velocity.compression.bottomOutCount ?? bottomF;
    bottomR = stR.velocity.compression.bottomOutCount ?? bottomR;
  }
  if (jOk && stF?.velocity?.rebound && stR?.velocity?.rebound) {
    topF = stF.velocity.rebound.topOutCount ?? topF;
    topR = stR.velocity.rebound.topOutCount ?? topR;
  }

  let dynSagFrontPct = null;
  let dynSagRearPct = null;
  if (metrics?.dynamic_sag_front != null && frontTravelMm > 0) {
    dynSagFrontPct = (metrics.dynamic_sag_front / frontTravelMm) * 100;
  }
  if (metrics?.dynamic_sag_rear != null && rearTravelMm > 0) {
    dynSagRearPct = (metrics.dynamic_sag_rear / rearTravelMm) * 100;
  }

  return {
    balanceRatio: metrics?.front_rear_balance_ratio ?? null,
    dynamicSagFrontMm: metrics?.dynamic_sag_front ?? null,
    dynamicSagRearMm: metrics?.dynamic_sag_rear ?? null,
    dynSagFrontPct,
    dynSagRearPct,
    travelFrontPct,
    travelRearPct,
    bottomF,
    bottomR,
    topF,
    topR,
    roughness:
      roughnessScore != null && Number.isFinite(Number(roughnessScore))
        ? Number(roughnessScore)
        : metrics?.harshness_index ?? null,
    packingIndex: jOk ? ar?.packingIndex : null,
    packingRating: jOk ? ar?.rating : null,
    jackOk: jOk,
  };
}
